/** The `itemErrors` area: the refusals of an item's contract commands and what hangs on it: relationships, module fields, statements, tags, checklists, replay (src/domain/state-machine.ts, src/server/work-items.ts, relationships.ts, modules.ts), as descriptors a surface formats
 *  (docs/I18N.md §6). The English is the refusal's `message`, unchanged. */
export const itemErrors = {
  // --- The contract commands (src/domain/state-machine.ts) ---------------------
  'itemErrors.contract.alreadyDelivered': 'Already marked done.',
  'itemErrors.contract.notDelivered': 'Not marked done; nothing to retract.',
  'itemErrors.contract.notClosed': 'Already open. Nothing to reopen.',
  'itemErrors.contract.alreadyActive': 'Already in progress.',
  'itemErrors.contract.alreadyIdle': 'Already not active.',
  'itemErrors.contract.alreadyContributor': 'You are already a contributor.',
  'itemErrors.contract.noRequesterToAccept': 'Unattributed work has no requester to accept it.',
  'itemErrors.contract.acceptNeedsDelivery': 'Can only accept work marked done.',
  'itemErrors.contract.alreadyAccepted': 'Already accepted.',
  'itemErrors.contract.noRequesterToDispute': 'Unattributed work has no requester to dispute it.',
  'itemErrors.contract.disputeNeedsDelivery': 'Can only dispute work marked done.',
  'itemErrors.contract.alreadyDisputed': 'Already disputed.',
  'itemErrors.contract.noRequesterToResolve': 'Unattributed work has no requester to resolve it.',
  'itemErrors.contract.noVerdict': 'No standing verdict to withdraw.',
  'itemErrors.contract.priorityUnchanged': 'Priority unchanged.',
  'itemErrors.contract.criteriaUnchanged': 'Acceptance criteria unchanged.',
  'itemErrors.contract.descriptionUnchanged': 'Description unchanged.',
  'itemErrors.contract.noteTooLong': 'A note is at most {max} characters (got {length}).',
  'itemErrors.contract.noteUnchanged': 'Note unchanged.',
  'itemErrors.contract.titleNeeded': 'An item needs a title.',
  'itemErrors.contract.titleUnchanged': 'Title unchanged.',
  'itemErrors.contract.visibilityUnchanged': 'Visibility unchanged.',
  'itemErrors.contract.inheritanceUnchanged': 'Inheritance already in that state.',
  'itemErrors.contract.reviewRequiredUnchanged': 'The review setting is already that.',
  'itemErrors.contract.requestersUnchanged': 'Requesters unchanged.',
  /** The contributor seat is already empty. */
  'itemErrors.contract.alreadyOpen': 'Already open.',
  'itemErrors.contract.contributorsUnchanged': 'Contributors unchanged.',
  'itemErrors.contract.alreadyTagged': 'Already tagged #{tag}.',
  'itemErrors.contract.commentEmpty': 'Comment is empty.',
  'itemErrors.contract.workItemTitleNeeded': 'A work item needs a title.',
  /** The idempotency key a create carries. */
  'itemErrors.contract.createKeyMalformed': 'That create carries a malformed key.',
  'itemErrors.contract.createKeyReused': 'That key already belongs to another act.',

  // --- Promises (src/domain/relationships.ts) ----------------------------------
  'itemErrors.promise.notADependency': 'Only a dependency (blocks) edge can become a promise.',
  'itemErrors.promise.pendingApproval':
    'This dependency is still pending approval; accept it first.',
  'itemErrors.promise.noRequesterToOwn':
    'This item has no requester to own the promise; seat a requester on it first.',
  'itemErrors.promise.noRequesterToAsk':
    'The dependency has no requester to ask; choose who to offer the promise to.',

  // --- Module fields (src/domain/module-kit.ts) --------------------------------
  // {label}: the field's own label, a flow author's name or a platform module's
  // English label, passed as it is.
  'itemErrors.field.unknown': 'Unknown field {module}.{field}.',
  'itemErrors.field.derived': '{label} is derived and cannot be set.',
  'itemErrors.field.singleValue': '{label} holds a single value.',
  'itemErrors.field.notANumber': '{label} must be a number.',
  'itemErrors.field.belowMin': '{label} must be at least {min}.',
  'itemErrors.field.aboveMax': '{label} must be at most {max}.',
  'itemErrors.field.notAllowed': '{label}: not an allowed value.',
  'itemErrors.field.notADate': '{label} must be a date.',
  'itemErrors.field.emptyRef': '{label}: empty reference.',
  /** {kinds}: the ref kinds joined with " or " ("person or group"), identifiers. */
  'itemErrors.field.wrongRefKind': '{label}: must reference a {kinds}.',
  'itemErrors.field.collectionNotEditable': '{label}: collection fields are not editable yet.',
  'itemErrors.field.notAList': '{label} must be a list.',
  'itemErrors.field.duplicateRow': '{label}: duplicate {rowKey} "{id}".',

  // --- Time values (src/domain/time-value.ts) ----------------------------------
  'itemErrors.time.empty': 'Empty time value.',
  'itemErrors.time.badRange': 'Unrecognized time range "{input}".',
  'itemErrors.time.mixedGrain': 'A range must use one grain on both ends.',
  'itemErrors.time.endBeforeStart': 'A range must end after it starts.',
  'itemErrors.time.unrecognized':
    'Unrecognized time "{input}". Try 2026-07-15, 2026-W24, 2026-07, 2026-Q3, 2026, or a/b range.',
  'itemErrors.time.noOffset':
    'The time "{input}" needs its offset from UTC, such as {input}Z or {input}+02:00.',

  // --- Qualifying a statement (src/domain/qualified-facts.ts) ------------------
  'itemErrors.qualify.badStance': '"{stance}" is not a qualification stance.',
  'itemErrors.qualify.ownDeclaration':
    "A declaration already colours its own item in its declarer's name - there is nothing to adopt.",
  'itemErrors.qualify.keyAndWord':
    'An adoption takes a catalog key or a word, never both - one declaration, one name.',
  'itemErrors.qualify.noKey': 'Adoption names the catalog key the statement is adopted as.',
  'itemErrors.qualify.badGrade': '"{grade}" is not a severity grade.',
  'itemErrors.qualify.wordNeedsGrade': 'Accepting a word states the grade it takes on.',
  'itemErrors.qualify.containmentColourless':
    'Containment is colourless: it carries no target key and no grade.',
  'itemErrors.qualify.ownStatementNotContainable':
    "An item's own statements are adoptable and notable, never containable, by anyone - containment declines the relay of someone else's statement, never the item's own truth.",

  // --- Tag names (src/domain/item-tags.ts) -------------------------------------
  'itemErrors.tag.nameNeeded': 'A tag needs a name.',
  'itemErrors.tag.tooLong': 'A tag is at most {max} characters.',
  'itemErrors.tag.emptySegment':
    'A tag path has no empty segment (no leading, trailing or double /).',
  'itemErrors.tag.badCharacters':
    'A tag holds letters, numbers, - and _ in any language, nested with /.',
  'itemErrors.tag.letterNeeded': 'A tag needs at least one letter.',

  // --- Sub-item order (src/domain/rank-order.ts) -------------------------------
  'itemErrors.order.notAChild': 'That item is not a child here.',
  'itemErrors.order.followsItself': 'An item cannot follow itself.',

  // --- Checklist lines (src/domain/checklist.ts) ------------------------------
  'itemErrors.line.badAct': 'That is not an act on a line.',
  'itemErrors.line.badId': 'That is not a line id.',
  'itemErrors.line.notFound': 'That line is not on this list.',
  'itemErrors.line.textNeeded': 'A line needs some text.',
  'itemErrors.line.tooLong': 'A line is at most {max} characters.',
  'itemErrors.line.tooMany': 'A list holds at most {max} lines.',
  'itemErrors.line.alreadyAdded': 'That line is already on the list.',
  'itemErrors.line.textUnchanged': 'The line already says that.',
  'itemErrors.line.ticked': 'That line is already ticked.',
  'itemErrors.line.open': 'That line is not ticked.',
  'itemErrors.line.nestUnchanged': 'The line is already there.',
  'itemErrors.line.underItself': 'A line cannot go under itself or a line under it.',
  'itemErrors.line.tickedNotPromoted': 'A ticked line is not made a sub-item.',
  'itemErrors.line.nestedNotPromoted': 'Only a line at the top of the list becomes a sub-item.',
  'itemErrors.line.nothingToPromote': 'The list has no open line at its top.',
  'itemErrors.line.notBare':
    'Only a sub-item with nothing but its title, under this item alone, can be made a line.',

  // --- Override mode (src/domain/elevation.ts) ---------------------------------
  /** {options}: the length keys joined with ", ", identifiers. */
  'itemErrors.elevation.unknownLength':
    'Unknown elevation length "{length}"; pick one of {options}.',
  'itemErrors.elevation.tooLong': 'An elevation may not last longer than a day.',
  'itemErrors.elevation.alreadyEnded': 'This elevation is already ended.',
  'itemErrors.elevation.alreadyExpired': 'This elevation has already expired.',

  // --- A queued gesture off the wire (src/domain/resolution/wire.ts) -----------
  // {type}, {kind}, {name}: the command, the gesture kind and the field name,
  // identifiers.
  'itemErrors.wire.noCommand': 'A queued act names no command.',
  'itemErrors.wire.notACommand': '"{type}" is not a command.',
  'itemErrors.wire.commandMissingField': 'The command {type} carries no {name}.',
  'itemErrors.wire.commandMalformedField': 'The command {type} carries a malformed {name}.',
  'itemErrors.wire.noTarget': 'A queued act names no target.',
  'itemErrors.wire.badTarget': 'A queued act names neither a command nor an attribute value.',
  'itemErrors.wire.notAGesture': 'A queued gesture is not a gesture.',
  'itemErrors.wire.gestureMalformedField': 'A queued {kind} carries a malformed {name}.',
  'itemErrors.wire.gestureMissingField': 'A queued {kind} carries no {name}.',
  'itemErrors.wire.unknownKind': 'A queued gesture names no kind the resolver knows.',
  'itemErrors.wire.noSurface': 'A queued gesture names no surface.',
  'itemErrors.wire.badExpectation': 'A queued gesture carries a malformed expectation.',
  // --- Creating an item (src/server/work-items.ts) -----------------------------
  'itemErrors.item.parentNotFound': 'Parent item not found.',
  'itemErrors.create.stageNeedsBoard': 'A stage column at birth needs a board placement.',
  'itemErrors.create.boardOtherOrg': 'Board belongs to a different org.',
  'itemErrors.create.boardHasNoStages': 'This board has no stage columns to create into.',
  'itemErrors.create.boardFlowRetired': "This board's flow is no longer active.",
  /** {stage}: the stage key the create named. */
  'itemErrors.create.noSuchStage': 'No stage "{stage}" in this board\'s flow.',
  'itemErrors.create.groupOtherOrg': 'Group belongs to a different org.',
  'itemErrors.create.parentOtherOrg':
    "A child is born in its parent's org; the parent belongs to a different one.",

  // --- Seats and offers (src/server/work-items.ts) -----------------------------
  'itemErrors.seat.replaceOwnerNeedsOwner':
    'Replacing the owner needs owner authority; ask an owner or request to take over.',
  'itemErrors.seat.alreadyHeld': 'They already hold that seat.',
  'itemErrors.seat.alreadyOffered': 'That seat is already offered to them.',
  'itemErrors.seat.groupMemberOnly':
    'You must be a member of the addressed group to take up this offer.',
  'itemErrors.seat.noOfferToYou': 'No open offer of that seat to you.',
  'itemErrors.seat.noOffer': 'No open offer of that seat.',
  'itemErrors.seat.declineNotYours':
    'Only the offeree (or a group member) may decline this offer, or the offerer withdraw it.',

  // --- Verdicts and closing (src/server/work-items.ts) -------------------------
  'itemErrors.verdict.followUpTitleNeeded': 'A follow-up needs a title.',
  'itemErrors.close.noItems': 'No items to close.',
  'itemErrors.close.openWorkUnder':
    'A parent in this cascade still has open work under it; nothing was closed.',
  // --- Relationships (src/server/relationships.ts) -----------------------------
  'itemErrors.relationship.selfRelation': 'An item cannot relate to itself.',
  'itemErrors.relationship.accessToRelate':
    'You need access to one of the two items to relate them.',
  /** {verb}: what the actor tried, an infinitive phrase (`verbRetype`, `verbChange`). */
  'itemErrors.relationship.accessTo': 'You need access to one of the two items to {verb}.',
  'itemErrors.relationship.verbRetype': 'retype the relationship',
  'itemErrors.relationship.verbChange': 'change the relationship',
  'itemErrors.relationship.cycle': 'That would create a cycle in the decomposition.',
  'itemErrors.relationship.cycleNow': 'That would now create a cycle in the decomposition.',
  'itemErrors.relationship.exists': 'That relationship already exists.',
  'itemErrors.relationship.alreadyRequested': 'That relationship is already requested.',
  'itemErrors.relationship.notPending': 'That relationship is not pending.',
  'itemErrors.relationship.acceptNeedsAccess':
    'Only someone with access to the protected side can accept.',
  'itemErrors.relationship.inheritanceOnParentOnly':
    'Only a parent edge carries access inheritance.',
  'itemErrors.relationship.noShare': 'This relationship carries no share.',
  'itemErrors.relationship.badShare': 'A share must be a non-negative number.',
  'itemErrors.relationship.parentNotRetypable':
    'Containment is structural; remove and re-link instead of retyping.',
  'itemErrors.relationship.stillPending':
    'This link is still pending - accept or withdraw it first.',
  'itemErrors.relationship.alreadyKind': 'Already that kind.',
  'itemErrors.relationship.conversionSamePair': 'A conversion keeps the same two items.',
  'itemErrors.relationship.alreadyLinkKind': 'Already that kind of link.',
  // --- Sections and their fields (src/server/modules.ts) -----------------------
  'itemErrors.module.noSuchFlow': 'No such flow in this org.',
  'itemErrors.module.flowArchived': 'That flow is archived; its fields no longer take values.',
  'itemErrors.module.noSuchField': 'No such field on that flow.',
  'itemErrors.module.privateField': 'A private flow field is an annotation, not item truth.',
  'itemErrors.module.notAMember':
    'This item is not a member of this flow; flow fields arrive by membership.',
  /** {module}: the module key, an identifier. */
  'itemErrors.module.unknown': 'Unknown module "{module}".',
  'itemErrors.module.valueUnchanged': 'Value unchanged.',
  'itemErrors.module.alreadyAdded': 'Module already added.',
  'itemErrors.module.alreadyAbsent': 'Module already absent.',
  // --- Renaming a tag (src/server/retag.ts) ------------------------------------
  'itemErrors.retag.sameName': 'That is already its name.',
  'itemErrors.retag.nothingToEdit': 'Nothing you can edit carries #{tag}.',
  'itemErrors.retag.tooMany':
    'A rename reaches at most {max} items at once; #{tag} is on {reach} you can edit.',

  // --- The offline replay (src/server/replay.ts) -------------------------------
  'itemErrors.replay.tooMany': 'A replay carries at most {max} commands.',
  'itemErrors.replay.rateLimited': 'Too many replays; try again shortly.',
  'itemErrors.replay.noCommand': 'A queued act carries no command.',
  'itemErrors.replay.notAnAct': 'A queued act is not an act.',
  'itemErrors.replay.malformedKey': 'A queued act carries a malformed key.',
  'itemErrors.replay.noItem': 'A queued act names no item.',
  'itemErrors.replay.malformedTime': 'A queued act carries a malformed time.',
  'itemErrors.replay.malformedBasis': 'A queued act carries a malformed basis.',

  // --- Notes on statements (src/server/statement-notes.ts) ---------------------
  'itemErrors.note.episodeGone':
    'That statement episode no longer holds; refresh and note the current statement.',

  // --- Checklist order (setChecklistOrder, src/server/views.ts) ---------------
  'itemErrors.checklist.memberOnly': 'Only a member of this board can change it.',
  // --- Direct assignment rules (src/server/push-rules.ts) ----------------------
  'itemErrors.pushRule.noCommons': 'No commons exists yet.',
  'itemErrors.pushRule.noSubject': 'A push rule needs a subject.',
  'itemErrors.pushRule.groupOwnerOnly': "Only a group's owner may set who can assign it directly.",
  'itemErrors.pushRule.selfOnly': 'Only you may set who can assign you directly.',
  'itemErrors.pushRule.alreadyDefault': 'Already on the default circle.',
  'itemErrors.pushRule.unchanged': 'Push rule unchanged.',

  // --- Override mode, ending it (src/server/elevation.ts) ----------------------
  'itemErrors.elevation.notElevated': 'You are not currently elevated.',

  // --- One gesture, one landing (src/server/event-log.ts) ----------------------
  'itemErrors.gesture.alreadyLanded': 'This gesture already landed; nothing was done twice.',

  // --- A statement a client names (src/read/statement-handles.ts) --------------
  'itemErrors.statement.gone': 'That statement no longer holds; refresh to see it as it is.',
  'itemErrors.statement.ambiguousCycle':
    'More than one cycle of this kind reaches this item; this act cannot tell them apart.',
  'itemErrors.statement.notAStatement': 'Not a statement.',

  // --- Attachments (src/domain/attachments.ts, docs/ATTACHMENTS.md §4) ---------
  'itemErrors.attachment.typeRefused': 'This kind of file is not accepted.',
  'itemErrors.attachment.tooLarge': 'This file is {size}. The limit per file is {limit}.',
  'itemErrors.attachment.dayExceeded':
    "With this file, today's files would pass the daily limit of {limit}.",
  'itemErrors.attachment.aliveExceeded':
    'Your files would pass the limit of {limit} in all. Remove some first.',
  'itemErrors.attachment.storeFull': 'The store has no room for this file.',
  'itemErrors.attachment.notFound': 'No such file.',
  'itemErrors.attachment.off': 'Files cannot be added on this instance.',
  /** The sniff saw an image, and the image library could not decode it. */
  'itemErrors.attachment.imageUnreadable': 'The image could not be read.',
  'itemErrors.attachment.empty': 'The file is empty.',
  /** A name that is blank, longer than 255 characters, or holds a slash or a control character. */
  'itemErrors.attachment.badName': 'This file name cannot be used.',
  /** Every upload slot of the instance is taken (the upload route's bound). */
  'itemErrors.attachment.busy':
    'Too many files are being uploaded right now. Try again in a moment.',
  /** The server's own disk refused the file on its way in. */
  'itemErrors.attachment.notReceived': 'The file could not be received. Try again later.',
} as const;
